import { useState, useRef, useEffect, useCallback, useImperativeHandle, forwardRef } from "react";
import { fetchCoachChat } from "@/services/coachTipService";

type Message = { role: "user" | "assistant"; content: string; timestamp: number };

/** Summary of a completed workout for AI review context. */
export type RecentRideSummary = {
  name: string;
  type: string;
  durationMin: number;
  rpe?: number;
  powerAvgWatts?: number;
  powerNpWatts?: number;
  heartRateAvg?: number;
  cadenceAvg?: number;
  distanceKm?: number;
  tssScore?: number;
  speedAvgKph?: number;
  completedAt: string;
  notes?: string;
};

/** Summary of a planned workout for the current week. */
export type WeekPlanEntry = { name: string; type: string; day: string; completed: boolean };

type CoachChatWidgetProps = {
  ftpWatts?: number;
  tsb?: number;
  ctl?: number;
  atl?: number;
  phase: string;
  daysToEvent?: number;
  readiness?: number;
  currentWorkoutType?: string;
  /** Recent completed rides for AI workout review (up to 15). */
  recentRides?: RecentRideSummary[];
  /** Current week's planned workouts. */
  weekPlan?: WeekPlanEntry[];
};

export type CoachChatHandle = {
  openWithMessage: (text: string) => void;
};

// ── Rate limit helpers ──────────────────────────────────────────────────────
const RATE_LIMIT_KEY = "coach-chat-sends";
const RATE_LIMIT_MAX = 20;
const RATE_LIMIT_WINDOW_MS = 60 * 60 * 1000; // 1 hour

function getRateLimitSends(): number[] {
  try {
    const raw = sessionStorage.getItem(RATE_LIMIT_KEY);
    if (!raw) return [];
    return (JSON.parse(raw) as number[]).filter((t) => Date.now() - t < RATE_LIMIT_WINDOW_MS);
  } catch { return []; }
}

function recordSend(): void {
  const sends = getRateLimitSends();
  sends.push(Date.now());
  try { sessionStorage.setItem(RATE_LIMIT_KEY, JSON.stringify(sends)); } catch { /* quota */ }
}

function getRemainingMessages(): number {
  return Math.max(0, RATE_LIMIT_MAX - getRateLimitSends().length);
}

// ── Message persistence ─────────────────────────────────────────────────────
const MSG_STORAGE_KEY = "coach-chat-messages";

function loadMessages(): Message[] {
  try {
    const raw = sessionStorage.getItem(MSG_STORAGE_KEY);
    if (!raw) return [];
    return JSON.parse(raw) as Message[];
  } catch { return []; }
}

function saveMessages(msgs: Message[]): void {
  try { sessionStorage.setItem(MSG_STORAGE_KEY, JSON.stringify(msgs.slice(-30))); } catch { /* quota */ }
}

// ── Timestamp formatting ────────────────────────────────────────────────────
function formatTime(ts: number): string {
  const diffMs = Date.now() - ts;
  if (diffMs < 60_000) return "just now";
  const diffMin = Math.floor(diffMs / 60_000);
  if (diffMin < 60) return `${diffMin} min ago`;
  return new Date(ts).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
}

// ── Response notification ───────────────────────────────────────────────────
function notifyResponse(): void {
  try { navigator.vibrate?.(50); } catch { /* unsupported */ }
  try {
    const ctx = new AudioContext();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.frequency.value = 800;
    gain.gain.value = 0.08;
    osc.start();
    osc.stop(ctx.currentTime + 0.08);
  } catch { /* silent fallback */ }
}

// ── Lightweight markdown renderer ───────────────────────────────────────────
function renderMarkdown(text: string): React.ReactNode[] {
  const lines = text.split("\n");
  const nodes: React.ReactNode[] = [];
  let listItems: string[] = [];

  const flushList = () => {
    if (listItems.length > 0) {
      nodes.push(
        <ul key={`ul-${nodes.length}`} className="list-disc list-inside space-y-0.5 my-1">
          {listItems.map((item, i) => <li key={i}>{inlineMd(item)}</li>)}
        </ul>,
      );
      listItems = [];
    }
  };

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    const bulletMatch = line.match(/^[-*]\s+(.+)/);
    if (bulletMatch) {
      listItems.push(bulletMatch[1]);
    } else {
      flushList();
      if (line.trim() === "") {
        if (nodes.length > 0) nodes.push(<br key={`br-${i}`} />);
      } else {
        nodes.push(<span key={`p-${i}`}>{inlineMd(line)} </span>);
      }
    }
  }
  flushList();
  return nodes;
}

function inlineMd(text: string): React.ReactNode {
  // Handle **bold** patterns
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  if (parts.length === 1) return text;
  return (
    <>
      {parts.map((part, i) => {
        const boldMatch = part.match(/^\*\*(.+)\*\*$/);
        if (boldMatch) return <strong key={i} className="font-semibold text-white">{boldMatch[1]}</strong>;
        return <span key={i}>{part}</span>;
      })}
    </>
  );
}

// ── Global toggle (used by MobileBottomNav to open chat without prop drilling) ─
const COACH_TOGGLE_EVENT = "coach-chat-toggle";
export function toggleCoachChat(): void {
  window.dispatchEvent(new CustomEvent(COACH_TOGGLE_EVENT));
}

// ── Suggestion chips ────────────────────────────────────────────────────────
const SUGGESTIONS = [
  "Review my last ride",
  "What should I eat?",
  "Stretching tips",
  "How are my legs?",
];

const NEW_USER_SUGGESTIONS = [
  "How should I warm up?",
  "What should I eat before a ride?",
  "Stretching for cyclists",
  "Help me set my FTP",
];

// ── Daily tips for new users (no rides yet) ─────────────────────────────────
const NEW_USER_TIPS = [
  {
    icon: "\u{1F9D8}",
    title: "Stretch It Out",
    text: "Hold each stretch 30-60s: hip flexors, hamstrings, quads, and calves. Tight hips are a cyclist's biggest enemy.",
  },
  {
    icon: "\u{1F34C}",
    title: "Pre-Ride Fuel",
    text: "Eat a carb-rich meal 2-3 hours before riding. A banana or energy bar 30 min before works for shorter sessions.",
  },
  {
    icon: "\u{1F6B2}",
    title: "Bike Fit Basics",
    text: "Saddle height: slight bend at the knee at the bottom of the pedal stroke. Handlebar reach: elbows slightly bent, relaxed shoulders.",
  },
  {
    icon: "\u{1F4A7}",
    title: "Stay Hydrated",
    text: "Drink 500ml in the 2 hours before a ride. On the bike, aim for a bottle per hour in warm conditions.",
  },
  {
    icon: "\u{1F4A4}",
    title: "Sleep Is Training",
    text: "7-9 hours of sleep is when your body repairs and adapts. Consistent bedtime matters more than total hours.",
  },
  {
    icon: "\u{1F9F4}",
    title: "Foam Rolling",
    text: "Spend 5 minutes rolling your quads, IT bands, and calves after rides. Slow rolls on tight spots — don't rush it.",
  },
  {
    icon: "\u{1F525}",
    title: "Warm Up Right",
    text: "Start every ride with 10 minutes of easy spinning before any effort. Cold muscles don't produce power safely.",
  },
  {
    icon: "\u{1F3AF}",
    title: "Set a Goal",
    text: "Pick a target event or fitness goal. Training with purpose — even a casual one — makes every session more effective.",
  },
];

function getDailyTip() {
  const dayOfYear = Math.floor(
    (Date.now() - new Date(new Date().getFullYear(), 0, 0).getTime()) / 86400000,
  );
  return NEW_USER_TIPS[dayOfYear % NEW_USER_TIPS.length];
}

// ── Component ───────────────────────────────────────────────────────────────
export const CoachChatWidget = forwardRef<CoachChatHandle, CoachChatWidgetProps>(function CoachChatWidget(
  {
    ftpWatts,
    tsb,
    ctl,
    atl,
    phase,
    daysToEvent,
    readiness,
    currentWorkoutType,
    recentRides,
    weekPlan,
  },
  ref,
) {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState<Message[]>(loadMessages);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [remaining, setRemaining] = useState(getRemainingMessages);
  const bottomRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const lastFailedRef = useRef<{ text: string; history: Message[] } | null>(null);

  // Typing animation state
  const [typingText, setTypingText] = useState<string | null>(null);
  const [typingFullText, setTypingFullText] = useState<string | null>(null);

  // Word-by-word typing animation
  useEffect(() => {
    if (typingFullText === null) return;
    const words = typingFullText.split(" ");
    let idx = 0;
    setTypingText("");
    const interval = setInterval(() => {
      idx++;
      if (idx >= words.length) {
        clearInterval(interval);
        // Push the full message and clear typing state
        setMessages((prev) => [...prev, { role: "assistant", content: typingFullText, timestamp: Date.now() }]);
        setTypingText(null);
        setTypingFullText(null);
        return;
      }
      setTypingText(words.slice(0, idx + 1).join(" "));
    }, 30);
    return () => clearInterval(interval);
  }, [typingFullText]);

  // Persist messages whenever they change
  useEffect(() => { saveMessages(messages); }, [messages]);

  // Listen for global toggle event from MobileBottomNav
  useEffect(() => {
    const handler = () => setOpen((v) => !v);
    window.addEventListener(COACH_TOGGLE_EVENT, handler);
    return () => window.removeEventListener(COACH_TOGGLE_EVENT, handler);
  }, []);

  useEffect(() => {
    if (open) setTimeout(() => inputRef.current?.focus(), 100);
  }, [open]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, loading, error, typingText]);

  // Imperative handle so DashboardPage can trigger a review message
  useImperativeHandle(ref, () => ({
    openWithMessage(text: string) {
      setOpen(true);
      // Small delay to let the panel render before sending
      setTimeout(() => {
        const userMsg: Message = { role: "user", content: text, timestamp: Date.now() };
        setMessages((prev) => {
          const next = [...prev, userMsg];
          void doSend(text, prev);
          return next;
        });
      }, 150);
    },
  }));

  const doSend = useCallback(async (text: string, history: Message[]) => {
    setError(null);
    setLoading(true);
    recordSend();
    setRemaining(getRemainingMessages());
    const result = await fetchCoachChat(text, history, {
      ftpWatts, tsb, ctl, atl, phase, daysToEvent, readiness, currentWorkoutType, recentRides, weekPlan,
    });
    setLoading(false);
    if (result.ok) {
      lastFailedRef.current = null;
      notifyResponse();
      // Start typing animation instead of setting message directly
      setTypingFullText(result.reply);
    } else {
      lastFailedRef.current = { text, history };
      setError(result.error);
    }
  }, [ftpWatts, tsb, ctl, atl, phase, daysToEvent, readiness, currentWorkoutType, recentRides, weekPlan]);

  const send = async () => {
    const text = input.trim();
    if (!text || loading) return;
    const userMsg: Message = { role: "user", content: text, timestamp: Date.now() };
    const currentHistory = [...messages];
    setMessages((prev) => [...prev, userMsg]);
    setInput("");
    await doSend(text, currentHistory);
  };

  const sendChip = (text: string) => {
    if (loading) return;
    const userMsg: Message = { role: "user", content: text, timestamp: Date.now() };
    const currentHistory = [...messages];
    setMessages((prev) => [...prev, userMsg]);
    void doSend(text, currentHistory);
  };

  const retry = () => {
    if (!lastFailedRef.current || loading) return;
    const { text, history } = lastFailedRef.current;
    setError(null);
    void doSend(text, history);
  };

  const clearChat = () => {
    setMessages([]);
    setError(null);
    setTypingText(null);
    setTypingFullText(null);
    lastFailedRef.current = null;
    try { sessionStorage.removeItem(MSG_STORAGE_KEY); } catch { /* ok */ }
  };

  const handleKey = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); void send(); }
  };

  return (
    <>
      {/* Desktop-only floating pill — hidden on mobile where Coach is in the bottom nav */}
      <button
        onClick={() => setOpen((v) => !v)}
        className="hidden sm:flex fixed bottom-6 right-6 z-40 rounded-lg bg-[rgb(var(--cp))] hover:bg-[rgb(var(--cp)/0.85)] text-white shadow-xl items-center justify-center transition-all px-4 py-2.5 gap-2 text-sm font-medium"
        aria-label="Open coach chat"
      >
        {open ? (
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        ) : (
          <svg className="w-4.5 h-4.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
          </svg>
        )}
        {open ? "Close" : "Coach"}
      </button>

      {/* Chat panel — above bottom nav on mobile, fixed box on desktop */}
      {open && (
        <div
          className="fixed inset-x-0 top-0 bottom-0 sm:inset-auto sm:bottom-20 sm:right-6 sm:w-[400px] sm:h-[460px] z-[60] bg-zinc-900 sm:border sm:border-zinc-800 sm:rounded-2xl shadow-2xl flex flex-col"
          style={{ height: "100dvh" }}
          // Mobile: full-screen overlay (z-60 above bottom nav). 100dvh adjusts
          // when the virtual keyboard opens so the input stays visible.
          // Desktop: sm: overrides reset to fixed 400×460 box.
        >
          {/* Header */}
          <div className="px-4 py-3 border-b border-zinc-800 flex items-center gap-2 flex-shrink-0">
            <div className="w-2 h-2 rounded-full bg-[rgb(var(--cp))] animate-coach-pulse" />
            <span className="text-sm font-semibold text-white">Coach</span>
            {ftpWatts && (
              <span className="text-[10px] text-zinc-500">
                FTP {ftpWatts}W
                {tsb !== undefined && (
                  <> · {tsb > 5 ? "Legs feel good" : tsb > -5 ? "Balanced" : tsb > -10 ? "Legs are heavy" : tsb > -20 ? "Tired" : "Need rest"}</>
                )}
              </span>
            )}
            <span className="ml-auto flex items-center gap-2">
              <span className="text-[10px] text-zinc-600">{remaining} left</span>
              {messages.length > 0 && (
                <button
                  onClick={clearChat}
                  className="text-zinc-500 hover:text-zinc-300 transition-colors"
                  title="Clear chat"
                  aria-label="Clear chat"
                >
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                </button>
              )}
              {/* Close button visible on mobile */}
              <button
                onClick={() => setOpen(false)}
                className="sm:hidden text-zinc-400 hover:text-white transition-colors"
                aria-label="Close chat"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </span>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto px-4 py-3 space-y-4" style={{ minHeight: 0 }}>
            {messages.length === 0 && typingText === null && (
              <div className="flex flex-col gap-4 mt-6">
                {(!recentRides || recentRides.length === 0) && (!weekPlan || weekPlan.length === 0) ? (
                  <>
                    <p className="text-[13px] text-zinc-400 text-center">
                      Here's a tip for today. Ask me anything about training, nutrition or recovery.
                    </p>
                    {/* Daily tip card */}
                    {(() => {
                      const tip = getDailyTip();
                      return (
                        <div className="rounded-xl bg-zinc-800/60 border border-zinc-700/40 px-4 py-3">
                          <div className="flex items-center gap-2 mb-1.5">
                            <span className="text-base">{tip.icon}</span>
                            <span className="text-[13px] font-semibold text-zinc-100">{tip.title}</span>
                          </div>
                          <p className="text-[12px] text-zinc-300 leading-relaxed">{tip.text}</p>
                        </div>
                      );
                    })()}
                    {/* New-user suggestion chips */}
                    <div className="flex flex-col gap-1.5">
                      {NEW_USER_SUGGESTIONS.map((s) => (
                        <button
                          key={s}
                          onClick={() => sendChip(s)}
                          disabled={loading}
                          className="w-full text-left text-[13px] px-3 py-2.5 rounded-lg bg-zinc-800/60 text-zinc-200 hover:bg-zinc-800 hover:text-white transition-colors disabled:opacity-40"
                        >
                          {s}
                        </button>
                      ))}
                    </div>
                  </>
                ) : (
                  <>
                    <p className="text-[13px] text-zinc-400 text-center">
                      Ask about your rides, review a workout, or get tips on food, sleep and recovery.
                    </p>
                    {/* Suggestion buttons — flat, full-width, no bubbles */}
                    <div className="flex flex-col gap-1.5">
                      {SUGGESTIONS.map((s) => (
                        <button
                          key={s}
                          onClick={() => sendChip(s)}
                          disabled={loading}
                          className="w-full text-left text-[13px] px-3 py-2.5 rounded-lg bg-zinc-800/60 text-zinc-200 hover:bg-zinc-800 hover:text-white transition-colors disabled:opacity-40"
                        >
                          {s}
                        </button>
                      ))}
                    </div>
                  </>
                )}
              </div>
            )}
            {messages.map((m, i) => (
              <div key={i}>
                {m.role === "user" ? (
                  <div>
                    <p className="text-[13px] text-zinc-300 mb-0.5">{m.content}</p>
                    <span className="text-[10px] text-zinc-600">{formatTime(m.timestamp)}</span>
                  </div>
                ) : (
                  <div className="border-l-2 border-[rgb(var(--cp))] pl-3">
                    <span className="block text-[10px] font-semibold text-[rgb(var(--cs))] uppercase tracking-wider mb-1">Coach</span>
                    <div className="text-[13px] text-zinc-100 leading-relaxed">
                      {renderMarkdown(m.content)}
                    </div>
                    <span className="block text-[10px] text-zinc-600 mt-1">{formatTime(m.timestamp)}</span>
                  </div>
                )}
              </div>
            ))}
            {/* Typing animation in progress */}
            {typingText !== null && (
              <div className="border-l-2 border-[rgb(var(--cp))] pl-3">
                <span className="block text-[10px] font-semibold text-[rgb(var(--cs))] uppercase tracking-wider mb-1">Coach</span>
                <div className="text-[13px] text-zinc-200 leading-relaxed">
                  {renderMarkdown(typingText)}
                </div>
              </div>
            )}
            {loading && typingText === null && (
              <div className="border-l-2 border-[rgb(var(--cp))] pl-3">
                <div className="flex gap-1.5 items-center py-1">
                  <span className="text-[11px] text-zinc-500">Coach is thinking</span>
                  <span className="w-1 h-1 bg-zinc-500 rounded-full animate-bounce" style={{ animationDelay: "0ms" }} />
                  <span className="w-1 h-1 bg-zinc-500 rounded-full animate-bounce" style={{ animationDelay: "150ms" }} />
                  <span className="w-1 h-1 bg-zinc-500 rounded-full animate-bounce" style={{ animationDelay: "300ms" }} />
                </div>
              </div>
            )}
            {error && (
              <div className="border-l-2 border-red-500/50 pl-3">
                <p className="text-xs text-red-400">{error}</p>
                <button
                  onClick={retry}
                  className="mt-1 text-[11px] text-zinc-400 hover:text-white underline underline-offset-2"
                >
                  Retry
                </button>
              </div>
            )}
            <div ref={bottomRef} />
          </div>

          {/* Input */}
          <div className="p-3 border-t border-zinc-800 flex gap-2 flex-shrink-0">
            <textarea
              ref={inputRef}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKey}
              placeholder="Ask your coach..."
              rows={1}
              className="flex-1 bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-sm text-white placeholder-zinc-500 focus:outline-none focus:border-[rgb(var(--cp))] resize-none"
            />
            <button
              onClick={() => void send()}
              disabled={!input.trim() || loading}
              className="px-3 py-2 bg-[rgb(var(--cp))] hover:bg-[rgb(var(--cp)/0.80)] disabled:opacity-40 text-white rounded-lg transition-colors flex-shrink-0 active:animate-bounce"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
              </svg>
            </button>
          </div>
        </div>
      )}
    </>
  );
});
